import { clamp01, frac } from '../../shared/scalar';
import { BEATS_PER_BAR } from '../../shared/tempo';
import { hash } from '../hash';
import { easeOutCubic } from '../motion';

const TAU = Math.PI * 2;

// Drawn into his mask's frames: calm mode shows the first alone, him swollen and still.
export const SWELL = 0.05;

export function swell(beats: number): number {
  const into = ((beats % BEATS_PER_BAR) + BEATS_PER_BAR) % BEATS_PER_BAR;
  return SWELL * Math.exp(-into * 4);
}

export const RAIN = { drops: 16, spread: 0.95, from: 0.4, fall: 0.85, speed: 1.6, calmSpeed: 0.8 };

export interface Spot {
  readonly x: number;
  readonly y: number;
  readonly alpha: number;
}

export function rainDrop(index: number, beats: number, calm: boolean): Spot {
  const column = -RAIN.spread + hash(index, 1) * 2 * RAIN.spread;
  const fall = frac(beats * (calm ? RAIN.calmSpeed : RAIN.speed) + hash(index, 2));
  return { x: column - fall * 0.15, y: RAIN.from + fall * RAIN.fall, alpha: 0.6 * (1 - fall) };
}

export const PUDDLE = { growBeats: 0.6, dryBeats: 8, splashBeats: 0.7, splashes: 8 };

export function puddleSize(ageBeats: number, leftBeats: number): number {
  const grow = easeOutCubic(clamp01(ageBeats / PUDDLE.growBeats));
  return grow * clamp01(leftBeats / PUDDLE.dryBeats);
}

const BLOB_POINTS = 11;

export function blobRadii(seed: number): number[] {
  return Array.from({ length: BLOB_POINTS }, (_, index) => 0.82 + hash(seed, index + 10) * 0.36);
}

export function splashDrop(index: number, ageBeats: number): Spot | null {
  const share = ageBeats / PUDDLE.splashBeats;
  if (share < 0 || share >= 1) {
    return null;
  }
  const angle = (index / PUDDLE.splashes) * TAU;
  const far = 0.15 + ageBeats * 1.4;
  return {
    x: Math.cos(angle) * far * 1.2,
    y: Math.sin(angle) * far * 0.7 - Math.sin(share * Math.PI) * 0.25,
    alpha: 1 - share,
  };
}

export const RINGS = 3;

export interface Ring {
  readonly x: number;
  readonly y: number;
  readonly age: number;
}

export function waterRing(seed: number, index: number, beats: number): Ring {
  const born = Math.floor(beats * 2) / 2 - index * 0.5;
  const key = Math.floor(born * 2);
  return {
    x: (hash(seed, key) - 0.5) * 1.6,
    y: (hash(seed, key + 1000) - 0.5) * 0.9,
    age: beats - born,
  };
}

export const ARCS = { count: 2, points: 5, radius: 14, waver: 4 };

export function arcPoints(trapId: number, index: number, beats: number, calm: boolean) {
  const roll = calm ? 0 : Math.floor(beats * 2);
  const seed = trapId * 31 + roll * 7 + index * 101;
  const start = hash(seed) * TAU;
  return Array.from({ length: ARCS.points }, (_, point): [number, number] => {
    const angle = start + point * 0.45;
    const radius = ARCS.radius + (point % 2 === 1 ? ARCS.waver : -1) + hash(seed, point + 1) * 3;
    return [Math.cos(angle) * radius, Math.sin(angle) * radius];
  });
}

export const EXIT_BEATS = 4;
export const SHREDS = 8;

export function rayAlpha(p: number): number {
  return 0.12 * Math.sin(clamp01(p) * Math.PI);
}

export function rainbowAlpha(p: number): number {
  return 0.55 * Math.sin(clamp01(p * 1.2) * Math.PI);
}

export interface Shred extends Spot {
  readonly scale: number;
}

export function shred(index: number, p: number): Shred {
  const angle = (index / SHREDS) * TAU;
  const far = 0.5 + p * 2.2;
  return {
    x: Math.cos(angle) * far,
    y: Math.sin(angle) * far * 0.6,
    alpha: 0.6 * (1 - p),
    scale: 1 - p * 0.5,
  };
}

export const SPARKS = 3;

export function spark(seed: number, index: number, beats: number): Spot {
  const rise = frac(beats * 1.5 + index / SPARKS + seed * 0.17);
  return { x: (hash(seed, index + 50) - 0.5) * 1.1, y: -rise * 0.75, alpha: 1 - rise };
}
